'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Option } from '../../../form-controls/types';
import type { PurchasedTicketsState } from '../../types';

const PURCHASED_TICKETS_RETURN_STATE_KEY = 'll-lottery-purchases-return-state';

type PurchasedTicketsReturnState = {
  tab: PurchasedTicketsState;
  scrollY: number;
};

let cachedReturnState: PurchasedTicketsReturnState | null | undefined;

function parseReturnState(raw: string | null): PurchasedTicketsReturnState | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PurchasedTicketsReturnState>;
    if ((parsed.tab === 'open' || parsed.tab === 'closed') && typeof parsed.scrollY === 'number' && Number.isFinite(parsed.scrollY)) {
      return { tab: parsed.tab, scrollY: parsed.scrollY };
    }
  } catch {
    return null;
  }

  return null;
}

function getReturnState(): PurchasedTicketsReturnState | null {
  if (cachedReturnState !== undefined) return cachedReturnState;

  cachedReturnState = typeof window !== 'undefined' ? parseReturnState(sessionStorage.getItem(PURCHASED_TICKETS_RETURN_STATE_KEY)) : null;
  sessionStorage.removeItem(PURCHASED_TICKETS_RETURN_STATE_KEY);

  return cachedReturnState;
}

function storeReturnState(tab: PurchasedTicketsState) {
  cachedReturnState = undefined;
  sessionStorage.setItem(PURCHASED_TICKETS_RETURN_STATE_KEY, JSON.stringify({ tab, scrollY: window.scrollY }));
}

function clearPurchasedTicketsReturnState() {
  cachedReturnState = undefined;
  sessionStorage.removeItem(PURCHASED_TICKETS_RETURN_STATE_KEY);
}

function resolveInitialOption(options: Option<PurchasedTicketsState>[], returnTab: PurchasedTicketsState | null) {
  if (!returnTab) return options[0]!;

  return options.find(option => option.value === returnTab) ?? options[0]!;
}

/**
 * Hook to manage the selected tab for the purchased tickets page.
 * @param options - The options for the tab switcher.
 * @returns The selected option, the ticket state, and the handlers for the tab switcher and pod click.
 */
function usePurchasedTicketsTabs(options: Option<PurchasedTicketsState>[]) {
  const [returnState] = useState(() => (typeof window !== 'undefined' ? getReturnState() : null));

  const [selectedOption, setSelectedOption] = useState<Option<PurchasedTicketsState>>(() => resolveInitialOption(options, returnState?.tab ?? null));

  const handleStateChange = (option: Option<PurchasedTicketsState>) => {
    setSelectedOption(option);
  };

  const handlePodClick = useCallback(() => {
    storeReturnState(selectedOption.value);
  }, [selectedOption.value]);

  return {
    selectedOption,
    ticketState: selectedOption.value,
    handleStateChange,
    handlePodClick,
    returnState,
  };
}

/**
 * Restore the scroll position of the purchased tickets page.
 * @param returnState - Parsed return state from usePurchasedTicketsTabs.
 * @param isContentReady - Whether the content is ready to be restored.
 */
function usePurchasedTicketsScrollRestore(returnState: PurchasedTicketsReturnState | null, isContentReady: boolean) {
  const hasRestoredScroll = useRef(false);

  useEffect(() => {
    if (!isContentReady || !returnState || hasRestoredScroll.current) return;

    hasRestoredScroll.current = true;

    requestAnimationFrame(() => {
      window.scrollTo(0, returnState.scrollY);
    });
  }, [isContentReady, returnState]);
}

export { clearPurchasedTicketsReturnState, usePurchasedTicketsScrollRestore, usePurchasedTicketsTabs };
